import { useState } from 'react';
import {
  DataTable,
  StatusField,
  Pagination,
  Icon,
  EmptyState,
  useSpinner,
  FiltersPanel,
  FilterField,
  Filter,
  Input,
  DateRangePicker,
} from '@chikara/ui';
import { DASHBOARD_MOVEMENT_COLUMNS, DASHBOARD_MOVEMENT_MOBILE_CONFIG, DASHBOARD_ITEMS_PER_PAGE } from '@/features/dashboard/constants/dashboard.constants';
import { Transaction } from '@/features/card/types/account.types';
import type {
  DashboardFilterCatalogs,
  DashboardFilterOption,
  DashboardFilters,
} from '@/features/dashboard/types/dashboard.types';
import { formatMXN } from '@/features/dashboard/utils/dashboard.utils';
import { getMinSelectableDate, hasAnyFilter } from '@/features/dashboard/utils/dashboardFilters.utils';
import emptyListIcon from '@/features/dashboard/assets/empty-list-icon.svg';
import { ReportChargebackModal } from '@/features/dashboard/components/ReportChargebackModal/ReportChargebackModal';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import styles from './DashboardMovements.module.css';
import { t } from '@/i18n';

/** Valor centinela para la opción "Todas/Todos" de los selects. */
const ALL_VALUE = 'all';

interface DashboardMovementsProps {
  data: Transaction[];
  total: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  /** Borrador en edición (lo que el usuario ve en los controles). */
  draftFilters: DashboardFilters;
  /** Actualiza el borrador con un patch parcial. */
  onFilterChange: (patch: Partial<DashboardFilters>) => void;
  /** Dispara la consulta con el borrador actual. */
  onApplyFilters: () => void;
  /** Limpia borrador y filtros aplicados. */
  onClearFilters: () => void;
  /** Catálogos dinámicos (BF-628). Sin valores, el select muestra solo "Todas/Todos". */
  catalogs?: DashboardFilterCatalogs;
  /** Si se provee, se muestra el botón "Descargar Excel" (export de BE, BF-616). */
  onExportExcel?: () => void | Promise<void>;
  /** Si se provee, el menú "Descargar" ofrece PDF (export de BE, BF-640). */
  onExportPdf?: () => void | Promise<void>;
}

const EMPTY_CATALOGS: DashboardFilterCatalogs = { cards: [], statuses: [], types: [] };

export function DashboardMovements({
  data,
  total,
  currentPage,
  onPageChange,
  draftFilters,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  catalogs = EMPTY_CATALOGS,
  onExportExcel,
  onExportPdf,
}: DashboardMovementsProps) {
  const { userData } = useAuth();
  const { isSpinnerLoading } = useSpinner();
  const basic = isBasic(userData.role);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const totalPages = Math.ceil(total / DASHBOARD_ITEMS_PER_PAGE);

  const today = new Date();
  const minDate = getMinSelectableDate(today);

  const withAllOption = (allLabel: string, options: DashboardFilterOption[]): DashboardFilterOption[] => [
    { label: allLabel, value: ALL_VALUE },
    ...options,
  ];

  const renderers: Record<string, (value: any, row: any) => React.ReactNode> = {
    amount: (value) => {
      const amount = typeof value === 'number' ? value : parseFloat(value as string);
      const formattedAmount = formatMXN(Math.abs(amount));
      return <span>{formattedAmount}</span>;
    },
    cdate: (value) => {
      const dateStr = value as string;
      if (!dateStr) return value;
      const [date, time] = dateStr.split(', ');
      return (
        <div className={styles.dateContainer}>
          <span className={styles.datePart}>{date}</span>
          {time && <span className={styles.datePart}>{time}</span>}
        </div>
      );
    },
    movement: (value, row) => {
      const isNegative = row.type === 'Abono';
      const isRejected = row.status === 'declined';
      const mov = value as any;

      const getIconName = () => {
        if (isRejected) return 'transaction-rejected';
        return isNegative ? 'transaction-out' : 'transaction-in';
      };

      const getIconContainerStyle = () => {
        if (isRejected) return styles.iconContainerRejected;
        return isNegative ? styles.iconContainerOut : styles.iconContainerIn;
      };

      return (
        <div className={styles.movementContainer}>
          <div className={getIconContainerStyle()}>
            <Icon
              name={getIconName()}
              size='2xl'
              color='inherit'
            />
          </div>

          <div className={styles.movementCell}>
            <div className={styles.movementDescription}>{mov.description}</div>
            <div className={styles.movementDetails}>
              {mov.subcategory}
            </div>
          </div>
        </div>
      );
    },
    status: (value, row) => (
      <StatusField
        status={row.statusName as string}
        statusType={value as any}
        actionLabel={row.report ? t('dashboard.reportAction') : undefined}
        showReportIcon={row.report}
        onActionClick={() => {
          setSelectedTransaction(row);
          setIsModalOpen(true);
        }}
        reportTooltip={row.report ? t('dashboard.reportTooltip') : undefined}
      />
    ),
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{basic ? t('dashboard.userMovementsTitle') : t('dashboard.teamMovementsTitle')}</h2>

      <FiltersPanel
        className={styles.filtersPanel}
        onApply={onApplyFilters}
        onClear={onClearFilters}
        hasCriteria={hasAnyFilter(draftFilters)}
        isApplying={isSpinnerLoading}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        hasResults={total > 0}
        applyLabel={t('dashboard.filters.apply')}
        clearLabel={t('dashboard.filters.clear')}
        exportLabel={t('dashboard.filters.download')}
      >
        <FilterField
          className={styles.filterField}
          label={t('dashboard.filters.emailLabel')}
          htmlFor="dashboard-filter-email"
        >
          <Input
            id="dashboard-filter-email"
            type="search"
            size="sm"
            leadingIcon={<Icon name="search" size="sm" color="inherit" />}
            placeholder={t('dashboard.filters.emailPlaceholder')}
            value={draftFilters.email ?? ''}
            onChange={(event) => onFilterChange({ email: event.target.value })}
          />
        </FilterField>

        <FilterField
          className={styles.filterField}
          label={t('dashboard.filters.cardLabel')}
          htmlFor="dashboard-filter-card"
        >
          <Filter
            id="dashboard-filter-card"
            size="sm"
            width="100%"
            value={draftFilters.cardLast4 ?? ALL_VALUE}
            onChange={(value) => onFilterChange({ cardLast4: value === ALL_VALUE ? undefined : value })}
            options={withAllOption(t('dashboard.filters.cardPlaceholder'), catalogs.cards)}
          />
        </FilterField>

        <FilterField
          className={styles.filterField}
          label={t('dashboard.filters.statusLabel')}
          htmlFor="dashboard-filter-status"
        >
          <Filter
            id="dashboard-filter-status"
            size="sm"
            width="100%"
            value={draftFilters.status ?? ALL_VALUE}
            onChange={(value) => onFilterChange({ status: value === ALL_VALUE ? undefined : value })}
            options={withAllOption(t('dashboard.filters.statusPlaceholder'), catalogs.statuses)}
          />
        </FilterField>

        <FilterField
          className={styles.filterField}
          label={t('dashboard.filters.typeLabel')}
          htmlFor="dashboard-filter-type"
        >
          <Filter
            id="dashboard-filter-type"
            size="sm"
            width="100%"
            value={draftFilters.type ?? ALL_VALUE}
            onChange={(value) => onFilterChange({ type: value === ALL_VALUE ? undefined : value })}
            options={withAllOption(t('dashboard.filters.typePlaceholder'), catalogs.types)}
          />
        </FilterField>

        <FilterField
          className={styles.filterField}
          label={t('dashboard.filters.dateLabel')}
          htmlFor="dashboard-filter-date"
        >
          <DateRangePicker
            id="dashboard-filter-date"
            value={{ from: draftFilters.from ?? null, to: draftFilters.to ?? null }}
            onChange={(range) => onFilterChange({ from: range.from, to: range.to })}
            minDate={minDate}
            maxDate={today}
            placeholder={t('dashboard.filters.datePlaceholder')}
          />
        </FilterField>
      </FiltersPanel>

      {(data?.length === 0 && !isSpinnerLoading) || !data ? (
        <EmptyState
          title={t('dashboard.emptyState.title')}
          description={basic ? t('dashboard.emptyState.descriptionBasicRole') : t('dashboard.emptyState.description')}
          imgSrc={emptyListIcon}
          maxWidth='460px'
        />
      ) : (
        <DataTable
          columns={DASHBOARD_MOVEMENT_COLUMNS}
          data={data}
          mobileConfig={DASHBOARD_MOVEMENT_MOBILE_CONFIG}
          isLoading={isSpinnerLoading}
          renderCell={(key, value, row) => {
            const renderer = renderers[key];
            return renderer ? renderer(value, row) : value;
          }}
        />
      )}
      {total > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          showInfo
          infoText={t('dashboard.paginationInfo', {
            start: (currentPage - 1) * DASHBOARD_ITEMS_PER_PAGE + 1,
            end: Math.min(currentPage * DASHBOARD_ITEMS_PER_PAGE, total),
            total: total
          })}
        />
      )}
      <ReportChargebackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transaction={selectedTransaction}
      />
    </section>
  );
}
